/**
 * The full-size transcript-ingestion review panel (modal overlay).
 *
 * A transcript ingestion can propose ~30 changes — too many for the chat-inline
 * `max-h` box (#6). Instead of cramming it into the chat, a Sources upload/
 * re-ingest opens this full-screen modal overlay: a wide, full-height panel with
 * a backdrop, a header naming the source, and the collapsed expand-on-demand
 * change list ({@link ProposalDiffReview} `layout="panel"`).
 *
 * Why a modal overlay (not a right-side drawer): the app-shell Care Delivery AI
 * chat is already docked on the right at the {@link AppContent} level, so a
 * right drawer would collide with it. A centered modal also lets this panel be
 * owned and opened entirely from within {@link SourcesPanel} — no AppContent
 * change is needed. Proposed clinical content is shown but never logged (HIPAA).
 */
import React, { useEffect } from "react";
import ProposalDiffReview from "./ProposalDiffReview";

/** Props for {@link ReviewPanel}. */
interface ReviewPanelProps {
  /** The ingestion run to review. */
  runId: string;
  /** The source document's title for the header (already name-resolved). */
  sourceTitle: string;
  /** Close the panel (Done / Escape / backdrop click). */
  onClose: () => void;
  /** Called once the run is fully resolved (e.g. to refresh the badge). */
  onResolved?: () => void;
}

/**
 * Render the full-size review panel as a modal overlay.
 *
 * @param props - The run id, source title, close handler, and resolved callback.
 * @returns The review-panel overlay element.
 */
export const ReviewPanel: React.FC<ReviewPanelProps> = ({
  runId,
  sourceTitle,
  onClose,
  onResolved,
}) => {
  // Escape closes the panel (a standard modal affordance).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-slate-900/40 p-4 sm:p-8"
      data-testid="review-panel-overlay"
    >
      <button
        type="button"
        aria-label="Close review"
        onClick={onClose}
        className="absolute inset-0 w-full h-full cursor-default"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Review changes for ${sourceTitle}`}
        data-testid="review-panel"
        className="relative z-10 w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-full overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Review ingested changes
            </div>
            <div className="text-base font-semibold text-slate-800 truncate">
              {sourceTitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Done reviewing"
            className="shrink-0 px-3 py-1.5 rounded border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50"
          >
            Done
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          <ProposalDiffReview
            runId={runId}
            layout="panel"
            onResolved={onResolved}
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewPanel;
